import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { DaysDataTable } from "@/components/days/days.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { daysApi } from "@/services/days/days.api";
import { $fetch } from "@/lib/fetch";
import { type DayFormDto, daysDtoSchema } from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { type DayDto } from "slde-api/dto-schemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DayForm } from "@/components/days/day.form";
import { toast } from "sonner";

export default function DaysPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [dayToUpdate, setDayToUpdate] = useState<DayDto | null>(null);

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: daysApi.days.queryKey(),
    queryFn: async () =>
      await $fetch(daysApi.days.route(), {
        output: daysDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createDay, isPending: isCreatingDay } = useMutation({
    mutationFn: async (form: DayFormDto) =>
      await $fetch(daysApi.days.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Dzień utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu dnia: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateDay, isPending: isUpdatingDay } = useMutation({
    mutationFn: async ({ id, ...form }: DayDto) =>
      await $fetch(daysApi.day.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano dzień");
      setDayToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji dnia: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteDay, isPending: isDeletingDay } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(daysApi.day.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Dzień został usunięty");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu dnia: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie dniami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj dzień
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <DaysDataTable
          data={items}
          actions={{
            onUpdateClick: (day) => setDayToUpdate(day),
            onDeleteClick: (id) => deleteDay({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!dayToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setDayToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!dayToUpdate ? "Edytuj dzień" : "Dodaj dzień"}
            </DialogTitle>
          </DialogHeader>

          <DayForm
            onSubmit={(form) =>
              !!dayToUpdate
                ? updateDay({ id: dayToUpdate.id, ...form })
                : createDay(form)
            }
            defaultValues={dayToUpdate}
            disabled={isCreatingDay || isUpdatingDay || isDeletingDay}
            loading={isCreatingDay || isUpdatingDay || isDeletingDay}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
